/**
 * Política al superar la cuota de un plan.
 *
 * OJO, discrepancia real del contrato (`docs/API.md`):
 *
 *  - el cuerpo de creación fija `onQuotaExceeded` con valores en español:
 *    `degradar | derivar | detener | excedente`;
 *  - la lectura que ya consumía el panel usa `onExceeded` con valores en
 *    inglés: `degrade | handoff | stop | overage`.
 *
 * Mientras no se unifique en el backend, el panel **envía** lo que el contrato
 * de creación manda y **acepta** los dos vocabularios al leer. Traducir aquí y
 * en un solo sitio es preferible a que cada vista adivine: el día que el
 * backend unifique, se borra la mitad de este archivo y nada más.
 */

/** Vocabulario de ESCRITURA: exactamente el del cuerpo de creación del contrato. */
export const ON_QUOTA_EXCEEDED = Object.freeze(['degradar', 'derivar', 'detener', 'excedente'])

/** Clave de i18n de cada política, por su valor canónico de escritura. */
export const QUOTA_POLICY_LABELS = Object.freeze({
  degradar: 'platform.onExceededDegrade',
  derivar: 'platform.onExceededHandoff',
  detener: 'platform.onExceededStop',
  excedente: 'platform.onExceededOverage',
})

/** Sinónimos aceptados al LEER, del vocabulario inglés al canónico. */
const ALIASES = {
  degrade: 'degradar',
  handoff: 'derivar',
  stop: 'detener',
  overage: 'excedente',
}

const TONES = {
  degradar: 'warning',
  derivar: 'info',
  detener: 'danger',
  excedente: 'neutral',
}

/**
 * Normaliza cualquiera de los dos vocabularios al canónico.
 * @param {string|null|undefined} value
 * @returns {string} valor canónico; `degradar` como opción por defecto conservadora
 */
export function normalizeQuotaPolicy(value) {
  if (ON_QUOTA_EXCEEDED.includes(value)) return value
  return ALIASES[value] ?? 'degradar'
}

/**
 * Descripción presentable de la política de un plan, venga con el nombre de
 * campo que venga.
 *
 * El tercer alias, `overage_policy`, es el que de verdad devuelve
 * `GET /platform/plans` (columna cruda de la tabla `plans`). Sin él, TODOS los
 * planes caían en el respaldo `degradar` y la pastilla de «al superar la cuota»
 * decía lo mismo en las tres tarjetas: un plan configurado para detener el
 * servicio se anunciaba como que sólo degradaba.
 *
 * @param {{ onQuotaExceeded?: string, onExceeded?: string, overage_policy?: string }} plan
 */
export function describeQuotaPolicy(plan = {}) {
  const raw = plan.onQuotaExceeded ?? plan.onExceeded ?? plan.overagePolicy ?? plan.overage_policy
  const key = normalizeQuotaPolicy(raw)
  return { key, labelKey: QUOTA_POLICY_LABELS[key], tone: TONES[key], declared: raw != null }
}

/**
 * Claves de `limits` en un plan, tal y como las valida `planLimitsSchema` en el
 * backend. Son las que se pintan y en este orden.
 *
 * Cada una lleva su clave de i18n y cómo se formatea. `model_spend_usd_month` es
 * dinero y el resto son recuentos: pintar un tope de gasto con el formateador de
 * números daba «250» donde debía poner «250,00 US$».
 *
 * `min` y `max` NO son adorno: son los mismos que declara `planLimitsSchema`.
 * Sin ellos el formulario dejaba escribir `users: 0` y `retention_months: 0`,
 * que el esquema rechaza (`min(1)`) — y el 400 llegaba sin decir qué campo era,
 * porque el formulario no había validado nada. Declararlos aquí, junto a las
 * claves, es lo que impide que el panel vuelva a separarse del contrato.
 */
export const PLAN_LIMITS = Object.freeze([
  {
    key: 'messages_per_month',
    labelKey: 'platform.limitMessages',
    kind: 'number',
    step: 1000,
    min: 0,
  },
  {
    key: 'conversations_per_month',
    labelKey: 'organization.limitConversations',
    kind: 'number',
    step: 100,
    min: 0,
  },
  {
    key: 'model_spend_usd_month',
    labelKey: 'organization.limitSpend',
    kind: 'money',
    step: 5,
    min: 0,
  },
  {
    key: 'whatsapp_sessions',
    labelKey: 'organization.limitSessions',
    kind: 'number',
    step: 1,
    min: 0,
  },
  // `users` y `retention_months` empiezan en 1 en el esquema: un plan con cero
  // usuarios o cero meses de retención no es un plan restrictivo, es un plan
  // que no se puede usar.
  { key: 'users', labelKey: 'organization.limitUsers', kind: 'number', step: 1, min: 1 },
  { key: 'documents', labelKey: 'platform.limitDocuments', kind: 'number', step: 50, min: 0 },
  { key: 'storage_mb', labelKey: 'platform.limitStorage', kind: 'number', step: 500, min: 0 },
  {
    key: 'retention_months',
    labelKey: 'platform.limitRetention',
    kind: 'number',
    step: 1,
    min: 1,
    max: 120,
  },
])

/** El tope de gasto en modelos: dinero, y por tanto sólo de plataforma. */
export const MODEL_SPEND_LIMIT = 'model_spend_usd_month'

/**
 * Los topes que se le enseñan al CLIENTE: los mismos menos el de gasto.
 *
 * El tope de gasto en modelos está en dólares y mide lo que a la plataforma le
 * cuesta servirle. Al lado de un plan de 49 US$/mes, un «tope de gasto: 20 US$»
 * no le dice al cliente nada que pueda usar y sí le dice lo que no es asunto
 * suyo. `/platform/plans` lo sigue enseñando entero, que es donde se configura.
 *
 * Es una lista aparte y no un filtro en la vista para que la separación viva en
 * el dato: `normalizePlanEnvelope` construye el plan del cliente recorriendo
 * ESTA lista, así que el importe ni siquiera llega a `/app`.
 */
export const CLIENT_PLAN_LIMITS = Object.freeze(
  PLAN_LIMITS.filter((entry) => entry.key !== MODEL_SPEND_LIMIT),
)

/**
 * ¿Está el valor dentro de lo que el backend acepta para ese tope?
 * @param {{ min?: number, max?: number }} limit
 * @param {unknown} value
 */
export function isLimitInRange(limit, value) {
  const amount = Number(value)
  if (!Number.isFinite(amount)) return false
  if (limit.min !== undefined && amount < limit.min) return false
  if (limit.max !== undefined && amount > limit.max) return false
  return true
}

/**
 * Plan presentable.
 *
 * De las cuatro cuotas que pintaba la tarjeta, TRES se leían con un nombre que
 * el backend no usa: `limits.conversations` por `conversations_per_month`,
 * `limits.spendUsd` por `model_spend_usd_month` y `limits.whatsappSessions` por
 * `whatsapp_sessions`. Sólo `users` coincidía. Y el precio se leía de `priceUsd`
 * cuando la columna es `price_usd_month`, así que los tres planes se anunciaban
 * gratis y sin límites.
 *
 * Los topes ausentes salen como `null` y NO como `0`. La diferencia es la más
 * cara de todo este archivo: en este contrato «sin tope» se representa omitiendo
 * la clave, y un «0» pintado ahí dice exactamente lo contrario de lo que pasa
 * —que el cliente no puede mandar ni un mensaje.
 *
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function normalizePlan(raw) {
  const source = object(raw)
  const limits = object(source.limits)

  return {
    id: text(source.id) || text(source.code),
    code: text(source.code),
    name: text(source.name) || text(source.code),
    description: nullable(source.description),
    priceUsd: number(source.priceUsd ?? source.price_usd_month ?? source.priceUsdMonth),
    keyMode: nullable(source.keyMode ?? source.llm_key_mode ?? source.llmKeyMode),
    overagePriceUsd: number(source.overagePriceUsd ?? source.overage_price_usd),
    billingMargin: number(source.billingMargin ?? source.billing_margin),
    isPublic: source.isPublic ?? source.is_public ?? null,
    archivedAt: nullable(source.archivedAt ?? source.archived_at),
    enabledChannels: Array.isArray(source.enabledChannels ?? source.enabled_channels)
      ? (source.enabledChannels ?? source.enabled_channels)
      : [],
    allowedModels: Array.isArray(source.allowedModels ?? source.allowed_models)
      ? (source.allowedModels ?? source.allowed_models)
      : [],
    // Se conservan las claves del contrato tal cual: son las que `PLAN_LIMITS`
    // recorre y las mismas que valida el backend al escribir.
    limits: Object.fromEntries(PLAN_LIMITS.map(({ key }) => [key, number(limits[key])])),
    overagePolicy: nullable(source.overagePolicy ?? source.overage_policy ?? source.onQuotaExceeded),
  }
}

/* ─── Utilidades internas ─────────────────────────────────────────────────── */

function object(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
}

function text(value) {
  if (typeof value === 'string') return value
  return typeof value === 'number' ? String(value) : ''
}

function nullable(value) {
  const result = text(value)
  return result === '' ? null : result
}

/** `null` cuando no hay dato, para poder distinguirlo de un cero real. */
function number(value) {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}
